// components/Modal/ModalStackContext.js
import { createContext, useContext, useState, useCallback } from 'react';

const ModalStackContext = createContext();

export const ModalStackProvider = ({ children }) => {
    const [modalStack, setModalStack] = useState([]);

    const pushModal = useCallback((modalConfig) => {
        setModalStack(prev => [...prev, {
            ...modalConfig,
            id: Date.now() + Math.random() // Unique ID for each modal
        }]);
    }, []);

    const popModal = useCallback(() => {
        setModalStack(prev => prev.slice(0, -1));
    }, []);

    const clearToModal = useCallback((modalType) => {
        setModalStack(prev => {
            const index = prev.findLastIndex(modal => modal.type === modalType);
            return index >= 0 ? prev.slice(0, index + 1) : [];
        });
    }, []);

    const clearAllModals = useCallback(() => {
        setModalStack([]);
    }, []);

    const replaceTopModal = useCallback((modalConfig) => {
        setModalStack(prev => [
            ...prev.slice(0, -1),
            { ...modalConfig, id: Date.now() + Math.random() }
        ]);
    }, []);

    const currentModal = modalStack[modalStack.length - 1] || null;
    const stackLength = modalStack.length;

    return (
        <ModalStackContext.Provider value={{
            modalStack,
            currentModal,
            stackLength,
            pushModal,
            popModal,
            clearToModal,
            clearAllModals,
            replaceTopModal
        }}>
            {children}
        </ModalStackContext.Provider>
    );
};

export const useModalStack = () => {
    const context = useContext(ModalStackContext);
    if (!context) {
        throw new Error('useModalStack must be used within a ModalStackProvider');
    }
    return context;
};

// Helper hook for calendar-specific modal management
export const useCalendarModals = () => {
    const { pushModal, popModal, clearAllModals, currentModal } = useModalStack();

    const openAddItem = useCallback((date, formState) => {
        pushModal({
            type: 'addItem',
            props: { date, formState }
        });
    }, [pushModal]);

    const openEditItem = useCallback((item, formState) => {
        pushModal({
            type: 'editItem',
            props: { item, formState }
        });
    }, [pushModal]);

    const openMultiSelect = useCallback((items, date) => {
        pushModal({
            type: 'multiSelect',
            props: { items, date }
        });
    }, [pushModal]);

    const openPreview = useCallback((previewType, props) => {
        pushModal({
            type: previewType, // 'coursePreview', 'simulationPreview', 'collectionPreview'
            props
        });
    }, [pushModal]);

    return {
        openAddItem,
        openEditItem,
        openMultiSelect,
        openPreview,
        closeModal: popModal,
        closeAllModals: clearAllModals,
        currentModal
    };
};